import { useState } from "react";
import { CheckCircle2, ExternalLink, Info, Loader2, ShieldCheck } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import type { IdentityCheckStatus } from "@/lib/api";

/** EF-3 — the optional identity check, the LAST step before /start: after
 *  the form (consent first) and screening, before the camera, fullscreen and
 *  the clock, so it never costs test time.
 *
 *  The check itself happens on the vendor's page in a NEW TAB — this tab
 *  stays mounted, which is what keeps the form's answers alive. Two clicks on
 *  purpose: the first creates the vendor session, the second is a real link
 *  the browser will never block as a pop-up, and it carries `noopener
 *  noreferrer` so the vendor tab cannot reach back into this one.
 *
 *  It NEVER blocks. Continue is always available, and "I don't want to do
 *  this check" is a visible route, not a dead end — that refusal path is what
 *  makes the consent freely given. What the candidate sees is deliberately
 *  coarse (`IdentityCheckStatus`): this card never says "failed"; the vendor
 *  page has already shown the person their result and the recruiter reads
 *  ours.
 *
 *  ⚠ Every sentence here is a PLACEHOLDER (wording is Emilia's; OPS-15: legal
 *  supplies the final wording before the feature is switched on). */
export function IdentityStep({
  status,
  url,
  opening,
  openError,
  onOpen,
  onContinue,
  onDecline,
  onBack,
  submitting,
  error,
}: {
  status: IdentityCheckStatus;
  /** The vendor page, once the session exists and can still be done. */
  url: string | null;
  opening: boolean;
  openError: string | null;
  onOpen: () => void;
  onContinue: () => void;
  onDecline: () => void;
  onBack: () => void;
  submitting: boolean;
  error: string | null;
}) {
  const [confirmingDecline, setConfirmingDecline] = useState(false);
  const done = status === "completed";
  const unavailable = status === "unavailable";
  const waiting = status === "pending" && url !== null;
  const settled = done || unavailable;

  return (
    <div className="flex min-h-full items-center justify-center bg-background p-6">
      <Card className="w-full max-w-lg">
        <CardHeader className="space-y-2">
          <p className="text-xs uppercase tracking-wide text-muted-foreground">
            Before you start
          </p>
          <CardTitle as="h1" className="text-2xl">
            Confirm your identity
          </CardTitle>
          <CardDescription>
            {/* OPS-15: legal supplies the final wording */}
            The recruiter asks candidates to confirm who they are before this
            test. It takes about a minute, and the timer hasn&rsquo;t started
            yet.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <ul className="list-disc space-y-1 pl-5 text-sm text-foreground/90">
            {/* OPS-15: legal supplies the final wording */}
            <li>
              You photograph an identity document and take a selfie, on our
              verification partner&rsquo;s page, in a new tab.
            </li>
            <li>
              The recruiter is told only whether the check passed and whether
              the name matches. We never receive or keep your document or
              photo.
            </li>
            <li>
              It&rsquo;s your choice. You can take the test without it, and the
              recruiter will see that you chose not to.
            </li>
          </ul>

          {!settled &&
            (url === null ? (
              <Button
                type="button"
                className="w-full min-h-11 cursor-pointer"
                onClick={onOpen}
                disabled={opening || submitting}
              >
                {opening ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                    Preparing your check…
                  </>
                ) : (
                  <>
                    <ShieldCheck className="mr-2 h-4 w-4" aria-hidden="true" />
                    Verify my identity
                  </>
                )}
              </Button>
            ) : (
              <Button asChild className="w-full min-h-11 cursor-pointer">
                <a href={url} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="mr-2 h-4 w-4" aria-hidden="true" />
                  Open the identity check
                  <span className="sr-only"> (opens in a new tab)</span>
                </a>
              </Button>
            ))}

          {/* One live region for every state, so a screen reader hears the
              change when the candidate comes back to this tab. */}
          <div aria-live="polite" className="min-h-6 text-sm">
            {done && (
              <p className="flex items-start gap-2 text-foreground">
                <CheckCircle2
                  className="mt-0.5 h-4 w-4 shrink-0 text-success"
                  aria-hidden="true"
                />
                <span>
                  Your identity check is complete. You can continue to the
                  test.
                </span>
              </p>
            )}
            {unavailable && (
              <p className="flex items-start gap-2 text-muted-foreground">
                <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                <span>
                  The identity check isn&rsquo;t available right now. You can
                  continue to the test — the recruiter will see that it
                  couldn&rsquo;t be run.
                </span>
              </p>
            )}
            {waiting && (
              <p className="flex items-start gap-2 text-muted-foreground">
                <Loader2
                  className="mt-0.5 h-4 w-4 shrink-0 animate-spin motion-reduce:animate-none"
                  aria-hidden="true"
                />
                <span>
                  Finish the check in the other tab, then come back here. This
                  page updates on its own.
                </span>
              </p>
            )}
          </div>

          {openError && (
            <p role="alert" className="text-sm text-destructive">
              {openError}
            </p>
          )}
          {error && (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          )}

          {!settled &&
            (confirmingDecline ? (
              <div className="space-y-3 rounded-md border border-border bg-muted/40 p-3">
                <p className="text-sm text-foreground/90">
                  {/* OPS-15: legal supplies the final wording */}
                  Continue without the identity check? You can still take the
                  test. The recruiter will see that you chose not to do it.
                </p>
                <div className="flex flex-col gap-2 sm:flex-row">
                  <Button
                    type="button"
                    variant="outline"
                    className="min-h-11 cursor-pointer sm:flex-1"
                    onClick={onDecline}
                    disabled={submitting}
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                        Starting…
                      </>
                    ) : (
                      "Continue without it"
                    )}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    className="min-h-11 cursor-pointer sm:flex-1"
                    onClick={() => setConfirmingDecline(false)}
                    disabled={submitting}
                  >
                    Keep the check
                  </Button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                className="min-h-11 cursor-pointer text-sm text-muted-foreground underline underline-offset-2 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                onClick={() => setConfirmingDecline(true)}
                disabled={submitting}
              >
                I don&rsquo;t want to do this check
              </button>
            ))}
        </CardContent>
        <CardFooter className="flex flex-col-reverse gap-2 sm:flex-row">
          <Button
            type="button"
            variant="outline"
            className="w-full min-h-11 cursor-pointer sm:w-auto"
            onClick={onBack}
            disabled={submitting}
          >
            Back
          </Button>
          {/* Shown once there is something to continue FROM: a finished or
              unavailable check, or one opened in the other tab (the vendor may
              still be deciding — the recruiter sees it as pending). Before
              that, the two ways forward are the check and the decline route. */}
          {(settled || waiting) && (
            <Button
              type="button"
              variant={settled ? "default" : "outline"}
              className="w-full min-h-11 cursor-pointer sm:flex-1"
              disabled={submitting || confirmingDecline}
              onClick={onContinue}
            >
              {submitting && !confirmingDecline ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                  Starting…
                </>
              ) : (
                "Continue to the test"
              )}
            </Button>
          )}
        </CardFooter>
      </Card>
    </div>
  );
}
